import React, { useState } from "react";
import { Plus, Pencil, Trash2, Shield, ShieldOff, Globe, Loader2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import DNSRecordFormModal from "./DNSRecordFormModal";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import tenantService from "../../services/tenant.service";

interface DNSRecord {
  id: string;
  name: string;
  type: string;
  content: string;
  proxied: boolean;
  created_on: string;
}

interface DNSRecordFormData {
  type: "CNAME" | "A" | "TXT";
  name: string;
  content: string;
  ttl: number;
  proxied: boolean;
}

interface DNSRecordsPanelProps {
  tenantKey: string;
  records: DNSRecord[];
  loading?: boolean;
  onRefresh: () => void;
}

const TYPE_BADGE: Record<string, string> = {
  CNAME: "bg-purple-100 text-purple-700",
  A: "bg-blue-100 text-blue-700",
  TXT: "bg-slate-100 text-slate-600",
};

const DNSRecordsPanel: React.FC<DNSRecordsPanelProps> = ({
  records,
  loading = false,
  onRefresh,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DNSRecord | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DNSRecord | null>(null);

  const handleAdd = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record: DNSRecord) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleDeleteClick = (record: DNSRecord) => {
    setDeleteTarget(record);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await tenantService.deleteDNSRecord(deleteTarget.id);
      toast.success("DNS record deleted");
      setDeleteTarget(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete DNS record");
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalSuccess = (_data: DNSRecordFormData) => {
    setModalOpen(false);
    const action = editingRecord ? "updated" : "created";
    toast.success(`DNS record ${action} successfully`);
    onRefresh();
  };

  return (
    <div>
      {/* Panel header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-slate-800">CloudFlare DNS Records</h3>
          <p className="text-xs text-slate-500 mt-0.5">{records.length} record{records.length !== 1 ? "s" : ""} configured</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all"
          >
            <Plus className="w-3 h-3" />
            Add Record
          </button>
        </div>
      </div>

      {/* Records table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : records.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Globe className="w-10 h-10 text-slate-300 mb-3" />
          <p className="text-sm font-semibold text-slate-600">No DNS records</p>
          <p className="text-xs text-slate-400 mt-1">Add a record to get started</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Hostname</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Target</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Proxy</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Created</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                        TYPE_BADGE[record.type] ?? "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {record.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{record.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 max-w-xs truncate">{record.content}</td>
                  <td className="px-4 py-3">
                    {record.proxied ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-orange-600">
                        <Shield className="w-3 h-3" /> Proxied
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <ShieldOff className="w-3 h-3" /> Direct
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(record.created_on).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(record)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(record)}
                        disabled={deletingId === record.id}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all disabled:opacity-50"
                        title="Delete"
                      >
                        {deletingId === record.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DNSRecordFormModal
        isOpen={modalOpen}
        editingRecord={editingRecord}
        onClose={() => setModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        iconName="delete"
        title="Delete DNS Record"
        description={`Are you sure you want to delete <strong>${deleteTarget?.name}</strong>? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default DNSRecordsPanel;
